import { OrderStatus } from '../common/constants'

export const getInstallmentsPaymentSummary = (order, refundedAmount = 0) => {
    const paidStatuses = [OrderStatus.Succeeded, OrderStatus.Refunded,
        OrderStatus.PartiallyRefunded, OrderStatus.WaitingForRefund, OrderStatus.RefundFailed]
    const grossPaid = (order.installments ?? []).reduce((sum, installment) =>
        sum + (paidStatuses.includes(installment.status) ? Number(installment.amount) || 0 : 0), 0)
    const paid = Math.max(0, grossPaid - refundedAmount)
    return { paid, remaining: Math.max(0, order.amount - paid) }
}

export const getInstallmentsTotal = (installments = []) => {
    return installments.reduce((sum, installment) => {
        if (installment?.status === OrderStatus.Refunded) return sum
        const amount = Number(installment?.amount)
        return sum + (Number.isNaN(amount) ? 0 : Math.round(amount))
    }, 0)
}

// Status changes (including manual refunds) do not create a new payment plan.
export const hasUnchangedInstallmentAmounts = (order, savedOrder) => {
    if (!savedOrder || Number(order?.amount) !== Number(savedOrder.amount)) return false
    const current = order?.installments ?? []
    const saved = savedOrder.installments ?? []
    if (current.length !== saved.length) return false
    const ids = new Set(current.map(item => item.id))
    if (ids.size !== current.length) return false
    return current.every(item => item.id != null && saved.some(original =>
        original.id === item.id && Number(original.amount) === Number(item.amount)))
}

export const isRefundedInstallment = (installment) =>
    [OrderStatus.Refunded, OrderStatus.PartiallyRefunded].includes(installment?.status)

export const isLockedRefundedInstallment = (installment, savedOrder) => {
    const saved = savedOrder?.installments?.find(item => item.id != null && item.id === installment?.id)
    return isRefundedInstallment(saved) && Boolean(savedOrder?.products?.some(product =>
        product.refunds?.some(refund => refund.stripe_id && refund.stripe_id !== 'MANUAL')))
}

export const isFixedInstallment = (installment) =>
    installment?.status === OrderStatus.Succeeded || isRefundedInstallment(installment)

export const updateInstallmentAmounts = (installments, index, amount, total, isLocked = isRefundedInstallment) => {
    if (isLocked(installments[index])) return installments
    const next = installments.map((item, i) => ({ ...item, amount: i === index ? amount : item.amount }))
    const adjustable = next.slice(index + 1).filter(item => !isFixedInstallment(item))
    const fixedTotal = getInstallmentsTotal(next.filter((item, i) => i <= index || isFixedInstallment(item)))
    if (adjustable.length) {
        const remaining = Math.max(0, total - fixedTotal)
        const base = Math.floor(remaining / adjustable.length)
        adjustable.forEach((item, i) => { item.amount = base + (i === 0 ? remaining % adjustable.length : 0) })
    }
    return next
}

export const parseRefundAmount = (value) => {
    const normalized = String(value ?? '').trim().replace(',', '.')
    if (!/^\d+(?:\.\d{0,2})?$/.test(normalized)) return null
    const cents = Math.round(Number(normalized) * 100)
    return Number.isSafeInteger(cents) && cents > 0 ? cents : null
}
